import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import logger from "@/lib/logger";
import { formatAED, formatDate, deadlineTone, daysBetween, periodLabel } from "@/lib/format";
import {
  Activity,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  AlertTriangle,
  CheckCircle2,
  Wallet,
  Bell,
  Inbox,
  ShieldPlus,
  FileText,
  Users as UsersIcon,
  ClipboardList,
  ClipboardCheck,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

const TYPE_COLORS = ["#1D4ED8", "#0EA5A5", "#F59E0B", "#7C3AED"];
const STATUS_COLORS = {
  "Not Started": "#94A3B8",
  "In Progress": "#1D4ED8",
  "Waiting for Client Information": "#F59E0B",
  "Ready for Review": "#6366F1",
  "Under Review": "#0EA5A5",
  "Completed": "#059669",
  "Overdue": "#DC2626",
};
const CHART_TICK = { fontSize: 11, fill: "#475569" };
const BAR_RADIUS = [6, 6, 0, 0];

const MODULES = [
  { key: "all", label: "All Compliances", source: "management", param: "all" },
  { key: "vat", label: "VAT and Accounting", source: "management", param: "vat" },
  { key: "excise", label: "Excise", source: "management", param: "excise" },
  { key: "pro", label: "PRO Compliance", source: "pro" },
  { key: "audit", label: "Audit and Other", source: "additional", param: "audit" },
  { key: "corporate-tax", label: "Corporate Tax", source: "additional", param: "corporate-tax" },
];

function KpiCard({ label, value, icon: Icon, hint, onClick, testid, tone = "slate" }) {
  const toneMap = {
    slate: "text-slate-500 bg-slate-100",
    blue: "text-blue-600 bg-blue-50",
    red: "text-red-600 bg-red-50",
    orange: "text-orange-600 bg-orange-50",
    yellow: "text-yellow-700 bg-yellow-50",
    green: "text-emerald-600 bg-emerald-50",
    violet: "text-violet-600 bg-violet-50",
  };
  return (
    <button
      onClick={onClick}
      className="kpi-card text-left w-full"
      data-testid={testid}
      type="button"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="kpi-label">{label}</div>
          <div className="kpi-value font-mono-num mt-2">{value}</div>
          {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
        </div>
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </button>
  );
}

function DeadlinePill({ deadline, status }) {
  const tone = deadlineTone(deadline, status);
  const d = daysBetween(deadline);
  const cls = { red: "dl-red", orange: "dl-orange", yellow: "dl-yellow", green: "dl-green", slate: "dl-slate" }[tone];
  let text;
  if (status === "Completed") text = "Completed";
  else if (d === 0) text = "Due today";
  else if (d > 0) text = `${d}d left`;
  else text = `${Math.abs(d)}d overdue`;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium font-mono-num ${cls}`}>
      {text}
    </span>
  );
}

function UpcomingList({ items, onOpen }) {
  if (!items?.length) {
    return <div className="text-sm text-slate-500 italic p-6">No records to show.</div>;
  }
  return (
    <ul className="divide-y divide-slate-200">
      {items.map((c) => (
        <li
          key={c.id}
          className="flex items-center justify-between py-3 px-2 hover:bg-slate-50 rounded-md cursor-pointer transition-colors"
          onClick={() => onOpen(c)}
          data-testid={`upcoming-item-${c.id}`}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                {c.compliance_type}
              </span>
              <span className="text-slate-300">•</span>
              <span className="text-sm font-medium text-slate-900 truncate">{c.client_name}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {periodLabel(c.return_period)} · {c.responsible_person?.name || "Unassigned"}
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0 pl-4">
            <span className="text-xs font-mono-num text-slate-600">{formatDate(c.deadline)}</span>
            <DeadlinePill deadline={c.deadline} status={c.status} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isManagement = user && ["administrator", "manager", "partner"].includes(user.role);
  const hasPro = user && (user.role === "administrator" || user.role === "manager" || user.pro_access);
  const [activeModule, setActiveModule] = useState("all");
  const [data, setData] = useState(null);
  const [proData, setProData] = useState(null);
  const [additionalData, setAdditionalData] = useState(null);
  const [loading, setLoading] = useState(true);

  const visibleModules = MODULES.filter((m) => m.key !== "pro" || hasPro);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const mod = MODULES.find((m) => m.key === activeModule);
        if (!mod || mod.source === "management") {
          const url = isManagement
            ? `/dashboard/management?module=${mod?.param || "all"}`
            : "/dashboard/employee";
          const { data } = await api.get(url);
          setData(data);
        } else if (mod.source === "pro") {
          const { data } = await api.get("/dashboard/pro");
          setProData(data);
        } else if (mod.source === "additional") {
          const { data } = await api.get(`/dashboard/additional?module=${mod.param}`);
          setAdditionalData(data);
        }
      } catch (err) { logger.error("dashboard load failed:", err); }
      finally { setLoading(false); }
    })();
  }, [activeModule, isManagement]);

  const goCompliances = (params) => {
    const search = new URLSearchParams(params).toString();
    nav(`/compliances${search ? "?" + search : ""}`);
  };
  const goExcise = () => nav("/excise");
  const goAudit = () => nav("/audit-compliance");
  const goCT = () => nav("/corporate-tax");
  const goPro = () => nav("/pro-compliances");

  const cards = data?.cards || {};
  const byTypeData = useMemo(() => (data?.by_type || []).map((d) => ({ name: d.type, value: d.count })), [data]);
  const byStatusData = useMemo(() => (data?.by_status || []).map((d) => ({ name: d.status, value: d.count })), [data]);
  const workloadData = useMemo(() => (data?.workload || []).slice(0, 8), [data]);

  const activeModuleObj = MODULES.find((m) => m.key === activeModule) || MODULES[0];
  const showManagementView = activeModuleObj.source === "management" && isManagement;
  const showEmployeeView = activeModuleObj.source === "management" && !isManagement;
  const showProView = activeModuleObj.source === "pro";
  const showAdditionalView = activeModuleObj.source === "additional";

  return (
    <AppLayout title="Dashboard">
      {/* Module tabs */}
      <div className="mb-6 -mt-1 flex flex-wrap items-center gap-2" data-testid="dashboard-module-tabs">
        {visibleModules.map((m) => (
          <button
            key={m.key}
            onClick={() => setActiveModule(m.key)}
            className={
              "px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors " +
              (activeModule === m.key
                ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
            }
            data-testid={`dashboard-tab-${m.key}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : showProView ? (
        <ProDashboardView data={proData} onOpen={goPro} />
      ) : showAdditionalView ? (
        <AdditionalDashboardView data={additionalData} module={activeModuleObj.param} onOpen={activeModuleObj.param === "corporate-tax" ? goCT : goAudit} />
      ) : isManagement ? (
        <div className="space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Total Active" value={cards.total_active ?? 0} icon={Activity} tone="blue"
              onClick={() => goCompliances({ excludeCompleted: 1 })} testid="kpi-total-active" />
            <KpiCard label="Due Today" value={cards.due_today ?? 0} icon={CalendarClock} tone="orange"
              onClick={() => goCompliances({ due_within: 0 })} testid="kpi-due-today" />
            <KpiCard label="Due Within 7 Days" value={cards.due_within_7_days ?? 0} icon={CalendarDays} tone="yellow"
              onClick={() => goCompliances({ due_within: 7 })} testid="kpi-due-7" />
            <KpiCard label="Due Within 30 Days" value={cards.due_within_30_days ?? 0} icon={CalendarRange} tone="slate"
              onClick={() => goCompliances({ due_within: 30 })} testid="kpi-due-30" />
            <KpiCard label="Overdue" value={cards.overdue ?? 0} icon={AlertTriangle} tone="red"
              onClick={() => goCompliances({ overdue_only: 1 })} testid="kpi-overdue" />
            <KpiCard label="Completed This Month" value={cards.completed_this_month ?? 0} icon={CheckCircle2} tone="green"
              onClick={() => goCompliances({ status: "Completed" })} testid="kpi-completed-month" />
            <KpiCard label="Total Fees (Active)" value={formatAED(cards.total_fees)} icon={Wallet} tone="violet"
              onClick={() => goCompliances({ excludeCompleted: 1 })} testid="kpi-total-fees" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-slate-900">Compliance by Type</h3>
              </div>
              <div className="h-64">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={byTypeData} innerRadius={55} outerRadius={90} dataKey="value" nameKey="name" paddingAngle={2}>
                      {byTypeData.map((entry, i) => (
                        <Cell key={entry.name} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-3 mt-2">
                {byTypeData.map((t, i) => (
                  <div key={t.name} className="flex items-center gap-1.5 text-xs text-slate-600">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: TYPE_COLORS[i % TYPE_COLORS.length] }} />
                    <span>{t.name}</span>
                    <span className="font-mono-num text-slate-800">· {t.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-6 lg:col-span-2">
              <h3 className="font-display font-semibold text-slate-900 mb-4">Workload by Responsible Person</h3>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={workloadData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" tick={CHART_TICK} interval={0} angle={-15} textAnchor="end" height={50} />
                    <YAxis tick={CHART_TICK} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#1D4ED8" radius={BAR_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-display font-semibold text-slate-900">Upcoming Deadlines (30 days)</h3>
                <button onClick={() => goCompliances({ due_within: 30 })} className="text-xs text-blue-700 font-medium hover:underline" data-testid="view-all-upcoming">
                  View all
                </button>
              </div>
              <div className="p-2">
                <UpcomingList items={data?.upcoming} onOpen={(c) => nav(`/compliances?open=${c.id}`)} />
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-display font-semibold text-slate-900">Overdue</h3>
                <button onClick={() => goCompliances({ overdue_only: 1 })} className="text-xs text-blue-700 font-medium hover:underline" data-testid="view-all-overdue">
                  View all
                </button>
              </div>
              <div className="p-2">
                <UpcomingList items={data?.overdue_list} onOpen={(c) => nav(`/compliances?open=${c.id}`)} />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-display font-semibold text-slate-900 mb-4">Compliance by Status</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
              {byStatusData.map((s) => (
                <button
                  key={s.name}
                  onClick={() => goCompliances({ status: s.name })}
                  className="text-left rounded-lg border border-slate-200 p-3 hover:border-blue-400 transition-colors"
                  data-testid={`status-tile-${s.name.replace(/\s+/g, "-")}`}
                >
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{s.name}</div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <div className="font-mono-num text-xl font-bold text-slate-900">{s.value}</div>
                    <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[s.name] || "#94A3B8" }} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="My Assigned" value={cards.my_assigned_compliances ?? 0} icon={Inbox} tone="blue"
              onClick={() => goCompliances({ mine_only: 1 })} testid="kpi-my-assigned" />
            <KpiCard label="Due Today" value={cards.due_today ?? 0} icon={CalendarClock} tone="orange"
              onClick={() => goCompliances({ mine_only: 1, due_within: 0 })} testid="kpi-due-today" />
            <KpiCard label="Due Within 7 Days" value={cards.due_within_7_days ?? 0} icon={CalendarDays} tone="yellow"
              onClick={() => goCompliances({ mine_only: 1, due_within: 7 })} testid="kpi-due-7" />
            <KpiCard label="Overdue" value={cards.overdue ?? 0} icon={AlertTriangle} tone="red"
              onClick={() => goCompliances({ mine_only: 1, overdue_only: 1 })} testid="kpi-overdue" />
            <KpiCard label="Ready for Review" value={cards.ready_for_review ?? 0} icon={Activity} tone="violet"
              onClick={() => goCompliances({ mine_only: 1, status: "Ready for Review" })} testid="kpi-ready-review" />
            <KpiCard label="Completed This Month" value={cards.completed_this_month ?? 0} icon={CheckCircle2} tone="green"
              onClick={() => goCompliances({ mine_only: 1, status: "Completed" })} testid="kpi-completed-month" />
            <KpiCard label="Unread Reminders" value={cards.unread_reminders ?? 0} icon={Bell} tone="red"
              onClick={() => nav("/reminders")} testid="kpi-unread-reminders" />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900">Upcoming Deadlines</h3>
              <button onClick={() => goCompliances({ mine_only: 1 })} className="text-xs text-blue-700 font-medium hover:underline">
                View all
              </button>
            </div>
            <div className="p-2">
              <UpcomingList items={data?.upcoming} onOpen={(c) => nav(`/compliances?open=${c.id}`)} />
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}


function ProDashboardView({ data, onOpen }) {
  if (!data) return <div className="text-sm text-slate-500">No PRO data available.</div>;
  return (
    <div className="space-y-6" data-testid="pro-dashboard-section">
      <div className="mb-4 flex items-center gap-2">
        <ShieldPlus className="w-5 h-5 text-violet-600" />
        <h2 className="font-display text-xl font-bold text-slate-900">PRO Compliance</h2>
        <button onClick={onOpen} className="ml-auto text-xs text-blue-700 font-medium hover:underline" data-testid="pro-view-all">
          Open PRO Compliance →
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard label="Total Active" value={data.cards?.total_active ?? 0} icon={Activity} tone="blue"
          onClick={onOpen} testid="pro-kpi-active" />
        <KpiCard label="Expiring in 7 Days" value={data.cards?.expiring_7 ?? 0} icon={CalendarClock} tone="orange"
          onClick={onOpen} testid="pro-kpi-7" />
        <KpiCard label="Expiring in 30 Days" value={data.cards?.expiring_30 ?? 0} icon={CalendarDays} tone="yellow"
          onClick={onOpen} testid="pro-kpi-30" />
        <KpiCard label="Expired" value={data.cards?.expired ?? 0} icon={AlertTriangle} tone="red"
          onClick={onOpen} testid="pro-kpi-expired" />
        <KpiCard label="Completed This Month" value={data.cards?.completed_this_month ?? 0} icon={CheckCircle2} tone="green"
          onClick={onOpen} testid="pro-kpi-completed" />
        <KpiCard label="Total Professional Fees" value={`AED ${(data.cards?.total_fees ?? 0).toLocaleString("en-US")}`} icon={Wallet} tone="violet"
          onClick={onOpen} testid="pro-kpi-fees" />
      </div>
      {data.upcoming?.length > 0 && (
        <div className="mt-6 bg-white border border-slate-200 rounded-xl">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="font-display font-semibold text-slate-900">Upcoming Expiries (30 days)</h3>
          </div>
          <ul className="divide-y divide-slate-200 p-2">
            {data.upcoming.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-3 px-2 hover:bg-slate-50 rounded-md cursor-pointer" onClick={onOpen}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{c.compliance_type}</span>
                    <span className="text-slate-300">•</span>
                    <span className="text-sm font-medium text-slate-900 truncate">{c.client_name}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.responsible_person?.name || "Unassigned"}</div>
                </div>
                <div className="text-xs font-mono-num text-slate-600">expires {new Date(c.expiry_date).toDateString()}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


const INVOICE_TONE_MAP = {
  "Pending": "bg-amber-50 text-amber-700 border-amber-200",
  "Invoiced": "bg-blue-50 text-blue-700 border-blue-200",
  "Partly Paid": "bg-orange-50 text-orange-700 border-orange-200",
  "Paid": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const ADD_STATUS_TONE_MAP = {
  "Pending": "bg-amber-50 text-amber-700 border-amber-200",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-200",
  "Under Review": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Completed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Filed": "bg-teal-50 text-teal-700 border-teal-200",
};

function AdditionalDashboardView({ data, module, onOpen }) {
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const cards = data.cards || {};
  const byTypeData = (data.by_type || []).map((d) => ({ name: d.type, value: d.count }));
  return (
    <div className="space-y-6" data-testid={`add-dashboard-${module}`}>
      <div className="mb-2 flex items-center gap-2">
        <ClipboardCheck className="w-5 h-5 text-indigo-600" />
        <h2 className="font-display text-xl font-bold text-slate-900">{data.title}</h2>
        <button onClick={onOpen} className="ml-auto text-xs text-blue-700 font-medium hover:underline">Open module →</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard label="Total Records" value={cards.total ?? 0} icon={FileText} tone="blue" onClick={onOpen} testid="add-kpi-total" />
        <KpiCard label="In Progress / Review" value={cards.in_progress ?? 0} icon={Activity} tone="orange" onClick={onOpen} testid="add-kpi-inprog" />
        <KpiCard label="Completed / Filed" value={cards.completed ?? 0} icon={CheckCircle2} tone="green" onClick={onOpen} testid="add-kpi-completed" />
        <KpiCard label="Unpaid Invoices" value={cards.unpaid_invoices ?? 0} icon={AlertTriangle} tone="red" onClick={onOpen} testid="add-kpi-unpaid" />
        <KpiCard label="Total Fees" value={formatAED(cards.total_fees)} icon={Wallet} tone="violet" onClick={onOpen} testid="add-kpi-fees" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-display font-semibold text-slate-900 mb-3">By Status</h3>
          <div className="space-y-2">
            {Object.entries(data.status_counts || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${ADD_STATUS_TONE_MAP[k] || "bg-slate-50 text-slate-700 border-slate-200"}`}>{k}</span>
                <span className="font-mono-num text-slate-900">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-display font-semibold text-slate-900 mb-3">Invoice Status</h3>
          <div className="space-y-2">
            {Object.entries(data.invoice_counts || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${INVOICE_TONE_MAP[k] || "bg-slate-50 text-slate-700 border-slate-200"}`}>{k}</span>
                <span className="font-mono-num text-slate-900">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-display font-semibold text-slate-900 mb-3">By Type</h3>
          <div className="h-52">
            <ResponsiveContainer>
              <BarChart data={byTypeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={CHART_TICK} interval={0} angle={-15} textAnchor="end" height={45} />
                <YAxis tick={CHART_TICK} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#6366F1" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {(data.workload || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-display font-semibold text-slate-900 mb-3 flex items-center gap-1.5"><UsersIcon className="w-4 h-4" /> Reviewer Workload (Active)</h3>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={data.workload.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={CHART_TICK} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={CHART_TICK} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#0EA5A5" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="px-6 py-4 border-b border-slate-200"><h3 className="font-display font-semibold text-slate-900">Active</h3></div>
          <ul className="divide-y divide-slate-200 p-2">
            {(data.active || []).length === 0 && <li className="p-4 text-sm text-slate-500">No active records.</li>}
            {(data.active || []).map((c) => (
              <li key={c.id} className="flex items-center justify-between py-3 px-2 hover:bg-slate-50 rounded-md cursor-pointer" onClick={onOpen}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{c.compliance_type}</span>
                    <span className="text-slate-300">•</span>
                    <span className="text-sm font-medium text-slate-900 truncate">{c.client_name}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.reviewer || c.person_1 || "Unassigned"}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md border font-semibold ${ADD_STATUS_TONE_MAP[c.status] || "bg-slate-50 text-slate-700 border-slate-200"}`}>{c.status}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="px-6 py-4 border-b border-slate-200"><h3 className="font-display font-semibold text-slate-900">Recent</h3></div>
          <ul className="divide-y divide-slate-200 p-2">
            {(data.recent || []).length === 0 && <li className="p-4 text-sm text-slate-500">No records yet.</li>}
            {(data.recent || []).map((c) => (
              <li key={c.id} className="flex items-center justify-between py-3 px-2 hover:bg-slate-50 rounded-md cursor-pointer" onClick={onOpen}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{c.compliance_type}</span>
                    <span className="text-slate-300">•</span>
                    <span className="text-sm font-medium text-slate-900 truncate">{c.client_name}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.period || "—"}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md border font-semibold ${INVOICE_TONE_MAP[c.invoice_status] || "bg-slate-50 text-slate-700 border-slate-200"}`}>{c.invoice_status}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
